import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { useApp } from './AppContext';
import type { Question } from './AppContext';
import type { Screen } from '../App';

interface SearchFieldProps {
  onNavigate: (screen: Screen) => void;
  onQuestionSelect?: (question: Question) => void; // Make optional for flexibility
  showOverlay?: boolean; // Whether to show results in overlay (default true)
  onSearchResults?: (results: Question[], query: string) => void; // Callback for search results
}

export const SearchField: React.FC<SearchFieldProps> = ({ 
  onNavigate, 
  onQuestionSelect, 
  showOverlay = true,
  onSearchResults 
}) => {
  const { searchQuestions } = useApp();
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchResults, setSearchResults] = useState<Question[]>([]);

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    if (searchQuery.trim()) {
      const results = searchQuestions(searchQuery, []); // Pass empty array for categories
      setSearchResults(results);
      setIsExpanded(showOverlay); // Only expand overlay if showOverlay is true
      
      // Call the callback if provided (for SearchScreen)
      if (onSearchResults) {
        onSearchResults(results, searchQuery);
      }
    } else {
      setSearchResults([]);
      setIsExpanded(false);
      
      // Call the callback with empty results
      if (onSearchResults) {
        onSearchResults([], searchQuery);
      }
    }
  };

  const handleClear = () => {
    setQuery('');
    setSearchResults([]);
    setIsExpanded(false);
    
    // Call the callback with empty results
    if (onSearchResults) {
      onSearchResults([], '');
    }
  };

  const handleQuestionClick = (question: Question) => {
    if (onQuestionSelect) {
      onQuestionSelect(question);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Математика': 'bg-blue-400/20 border-blue-400/30 text-blue-200 dark:bg-blue-400/15 dark:border-blue-400/25',
      'Физика': 'bg-green-400/20 border-green-400/30 text-green-200 dark:bg-green-400/15 dark:border-green-400/25',
      'Химия': 'bg-purple-400/20 border-purple-400/30 text-purple-200 dark:bg-purple-400/15 dark:border-purple-400/25',
      'География': 'bg-orange-400/20 border-orange-400/30 text-orange-200 dark:bg-orange-400/15 dark:border-orange-400/25',
      'История': 'bg-yellow-400/20 border-yellow-400/30 text-yellow-200 dark:bg-yellow-400/15 dark:border-yellow-400/25',
      'Литература': 'bg-pink-400/20 border-pink-400/30 text-pink-200 dark:bg-pink-400/15 dark:border-pink-400/25',
      'Биология': 'bg-emerald-400/20 border-emerald-400/30 text-emerald-200 dark:bg-emerald-400/15 dark:border-emerald-400/25',
      'default': 'glass-badge-default'
    };
    return colors[category] || colors.default;
  };

  return (
    <>
      {/* Search Input - integrated in header */}
      <div className="flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4 landscape-compact-p-2 h-12 sm:h-14 md:h-16 landscape-compact-h-10 dark:bg-black/30 dark:border-white/15 w-full min-w-0 max-w-full">
        <Search className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 landscape-compact-h-4 landscape-compact-w-4 glass-icon flex-shrink-0" />
        
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Поиск вопросов по ключевым словам..."
          className="flex-1 bg-transparent border-none outline-none glass-text placeholder-white/60 dark:placeholder-white/40 text-sm sm:text-base md:text-lg landscape-compact-text-sm search-input-fixed min-w-0 max-w-full"
          style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
        />
        
        {query && (
          <button
            onClick={handleClear}
            className="arrow-button-sm flex-shrink-0"
            style={{ width: '36px', height: '36px', padding: '8px' }}
            aria-label="Очистить поиск"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5 landscape-compact-h-3 landscape-compact-w-3 arrow-icon" />
          </button>
        )}
      </div>

      {/* Search Results Portal - render to parent container */}
      {showOverlay && isExpanded && query && (
        <div className="search-overlay glass-bg">
          {/* Positioned below fixed header with responsive offset */}
          
          {searchResults.length > 0 ? (
            /* Scrollable results */
            <div className="h-full overflow-y-auto px-4 sm:px-5 md:px-6 lg:px-7 landscape:px-3 landscape-compact:px-2 pb-4">
              <div className="space-y-3 pt-3">
                {searchResults.map((question) => (
                  <div
                    key={question.id}
                    className="glass-interactive rounded-lg p-3 cursor-pointer group"
                    onClick={() => handleQuestionClick(question)}
                  >
                    <div className="mb-2">
                      <h4 className="text-sm sm:text-base glass-text line-clamp-2 mb-1">
                        {question.question}
                      </h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-xs px-2 py-1 rounded-full border backdrop-blur-sm whitespace-nowrap ${getCategoryColor(question.category)}`}>
                          {question.category}
                        </Badge>
                      </div>
                    </div>

                    {/* Preview of answers */}
                    <div className="text-xs glass-text-muted">
                      Варианты: {question.options.slice(0, 2).join(', ')}
                      {question.options.length > 2 && '...'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* No results message */
            <div className="h-full flex items-center justify-center px-4">
              <div className="text-center">
                <Search className="h-8 w-8 glass-text-muted mx-auto mb-2 opacity-50" />
                <p className="text-sm glass-text-secondary">
                  По запросу "{query}" ничего не найдено
                </p>
                <p className="text-xs glass-text-muted mt-1">
                  Попробуйте изменить ключевые слова
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};